import { useEffect, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import Reveal from "./Reveal";

const fmt = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

function AnimatedEuro({ value }) {
  const spring = useSpring(value, { stiffness: 90, damping: 18 });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  const display = useTransform(spring, (v) => fmt(Math.max(0, v)));
  return <motion.span className="font-jbmono">{display}</motion.span>;
}

function SliderRow({ label, value, min, max, step, unit, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm text-slate-300">{label}</label>
        <span className="font-jbmono text-sm text-emerald-300">
          {value}
          <span className="text-slate-500 ml-1">{unit}</span>
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        aria-label={label}
      />
    </div>
  );
}

export default function Calculator() {
  const [advisors, setAdvisors] = useState(5);
  const [contracts, setContracts] = useState(12);
  const [commission, setCommission] = useState(60);

  const monthly = advisors * contracts * commission;
  const yearly = monthly * 12;
  const threeYears = yearly * 3;

  const cards = [
    { label: "Revenu mensuel de l'agence", value: monthly, testid: "calc-monthly" },
    { label: "Revenu annuel projeté", value: yearly, testid: "calc-yearly" },
    { label: "Projection cumulée 3 ans", value: threeYears, testid: "calc-3years" },
  ];

  return (
    <section id="simulation" data-testid="revenue-calculator" className="relative py-28 md:py-36 border-t border-emerald-400/10 overflow-hidden">
      <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start relative">
        <Reveal>
          <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">04 — La simulation</p>
          <h2 className="font-display font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl mb-6">
            Combien peut rapporter
            <span className="text-emerald-400"> ton agence ?</span>
          </h2>
          <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-12 max-w-lg">
            Trois curseurs, une projection instantanée. Ajuste ton équipe, ton rythme de
            signature et ton panier moyen — le modèle fait le reste.
          </p>

          <div className="space-y-9 rounded-[1.75rem] border border-emerald-400/15 bg-[#0E1713]/70 p-8 md:p-10">
            <SliderRow label="Commerciaux dans ton équipe" value={advisors} min={1} max={20} step={1} unit="pers." onChange={setAdvisors} testid="calc-slider-advisors" />
            <SliderRow label="Contrats signés / mois / commercial" value={contracts} min={1} max={60} step={1} unit="ctr." onChange={setContracts} testid="calc-slider-contracts" />
            <SliderRow label="Commission moyenne par contrat / vendeur" value={commission} min={10} max={150} step={5} unit="€" onChange={setCommission} testid="calc-slider-commission" />
          </div>
        </Reveal>

        <div className="space-y-5 lg:sticky lg:top-28">
          {cards.map((c, i) => (
            <Reveal key={c.label} delay={0.1 + i * 0.1}>
              <div
                data-testid={c.testid}
                className={`rounded-[1.75rem] border p-8 md:p-10 transition-colors duration-500 ${
                  i === 2
                    ? "border-emerald-400/40 bg-emerald-400/10 shadow-[0_30px_90px_-40px_rgba(16,185,129,0.5)]"
                    : "border-emerald-400/15 bg-[#0E1713]/70"
                }`}
              >
                <p className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-3">{c.label}</p>
                <p className={`font-bold text-4xl md:text-5xl tracking-tight ${i === 2 ? "text-emerald-300" : "text-slate-100"}`}>
                  <AnimatedEuro value={c.value} />
                </p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <p className="text-xs text-slate-600 leading-relaxed px-2">
              Projection indicative et non contractuelle, basée sur les moyennes constatées du
              réseau Alternativ. Tes résultats dépendent de ton territoire et de ton implication.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
